import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { appsyncQuery } from '../../../api/appsync-client.js'
import { sortTasks } from '../../../components/tasks/utils/sorting.js'
import { TASKS_FOR_MY_PAIRS } from '../graphql.js'
import { readTasksUrlState, writeTasksUrlState } from '../utils/urlState.js'

const PAGE_SIZE = 50

const OPEN_STATUSES = ['TODO', 'IN_PROGRESS']
const PAST_STATUSES = ['DONE', 'CANCELLED']

function statusesForTab(tab) {
  return tab === 'past' ? PAST_STATUSES : OPEN_STATUSES
}

function isOverdue(task, today) {
  if (!task.dueDate) return false
  if (task.status !== 'TODO' && task.status !== 'IN_PROGRESS') return false
  const [y, m, d] = task.dueDate.split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1) < today
}

/**
 * The brain of the Tasks page.
 *
 * Fetches `tasksForMyPairs` once per tab (Open / Past), keeps the full
 * result set in memory, applies filters + sort + client-side pagination
 * locally, exposes optimistic mutators for bulk actions, and syncs
 * filter / sort / tab state to and from URL query params.
 *
 * `tasksForMyPairs` returns every task across every active pair the
 * caller is part of, regardless of who the assignee is. The page-level
 * "Me / Others" assignee filter then narrows the in-memory set — that
 * gives "Others" something real to filter, which the older
 * `tasksAssignedToMe` query (assignee-keyed GSI) couldn't.
 *
 * Subscriptions are NOT mounted here — the page renders <PairSubscriber>
 * children that forward events to the handlers exposed below.
 */
/**
 * Does the assignee filter list constrain the task set?
 * Returns false (= no constraint) when:
 *   - the list is null or empty
 *   - it includes both 'me' and 'others' (full coverage)
 */
function assigneeMatches(list, taskAssignee, currentUserId) {
  if (!list || list.length === 0) return true
  const wantsMe = list.includes('me')
  const wantsOthers = list.includes('others')
  if (wantsMe && wantsOthers) return true
  const isMe = taskAssignee === currentUserId
  if (wantsMe) return isMe
  if (wantsOthers) return !isMe
  return true
}

export function useTasksPage({ currentUserId } = {}) {
  // Hydrate filter / sort / tab from URL on mount.
  const initial = useMemo(() => readTasksUrlState(), [])
  const [tab, setTabState] = useState(initial.tab)
  const [filters, setFiltersState] = useState({
    statuses: initial.statuses,
    priorities: initial.priorities,
    assignee: initial.assignee,
  })
  const [sortField, setSortFieldState] = useState(initial.sortField)
  const [sortDirection, setSortDirectionState] = useState(initial.sortDirection)

  const [openTasks, setOpenTasks] = useState([])
  const [pastTasks, setPastTasks] = useState([])
  const [openLoaded, setOpenLoaded] = useState(false)
  const [pastLoaded, setPastLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [selectedIds, setSelectedIds] = useState([])

  // ── URL sync ───────────────────────────────────────────────────────────
  const writeUrl = useCallback(() => {
    writeTasksUrlState({
      tab,
      ...filters,
      sortField,
      sortDirection,
    })
  }, [tab, filters, sortField, sortDirection])

  useEffect(() => {
    writeUrl()
  }, [writeUrl])

  // ── Fetch ──────────────────────────────────────────────────────────────
  const fetchForTab = useCallback(
    async (whichTab) => {
      if (!currentUserId) return
      setLoading(true)
      setError(null)
      try {
        const data = await appsyncQuery(TASKS_FOR_MY_PAIRS, {
          statusFilter: statusesForTab(whichTab).join(','),
        })
        const items = data?.tasksForMyPairs ?? []
        if (whichTab === 'past') {
          setPastTasks(items)
          setPastLoaded(true)
        } else {
          setOpenTasks(items)
          setOpenLoaded(true)
        }
      } catch (err) {
        setError(err)
      } finally {
        setLoading(false)
      }
    },
    [currentUserId],
  )

  useEffect(() => {
    fetchForTab(tab)
    // Reset client-side pagination on tab change.
    setVisibleCount(PAGE_SIZE)
    setSelectedIds([])
  }, [tab, fetchForTab])

  const refetch = useCallback(() => fetchForTab(tab), [fetchForTab, tab])

  // ── Subscription event handlers (the page wires PairSubscriber children to these) ──
  const mergeTask = useCallback(
    (task) => {
      if (!task) return
      const target = OPEN_STATUSES.includes(task.status) ? 'open' : 'past'
      const setter = target === 'open' ? setOpenTasks : setPastTasks
      setter((prev) => {
        const exists = prev.some((t) => t.taskId === task.taskId)
        if (exists) return prev.map((t) => (t.taskId === task.taskId ? task : t))
        return [...prev, task]
      })
      // If status changed across the open/past divide, remove from the other tab.
      const other = target === 'open' ? setPastTasks : setOpenTasks
      other((prev) => prev.filter((t) => t.taskId !== task.taskId))
    },
    [],
  )

  const handleCreated = useCallback((task) => mergeTask(task), [mergeTask])
  const handleUpdated = useCallback((task) => mergeTask(task), [mergeTask])
  const handleDeleted = useCallback((task) => {
    if (!task) return
    setOpenTasks((prev) => prev.filter((t) => t.taskId !== task.taskId))
    setPastTasks((prev) => prev.filter((t) => t.taskId !== task.taskId))
    setSelectedIds((prev) => prev.filter((id) => id !== task.taskId))
  }, [])

  // ── Filter + sort + paginate ───────────────────────────────────────────
  const sourceForTab = tab === 'past' ? pastTasks : openTasks

  const filtered = useMemo(() => {
    let result = sourceForTab
    if (filters.statuses && filters.statuses.length > 0) {
      const allowed = new Set(filters.statuses.map((s) => s.toUpperCase()))
      result = result.filter((t) => allowed.has(t.status))
    }
    if (filters.priorities && filters.priorities.length > 0) {
      const allowed = new Set(filters.priorities.map((p) => p.toUpperCase()))
      result = result.filter((t) => allowed.has(t.priority))
    }
    result = result.filter((t) => assigneeMatches(filters.assignee, t.assignee, currentUserId))
    return sortTasks(result, sortField, sortDirection)
  }, [sourceForTab, filters, sortField, sortDirection, currentUserId])

  const visibleTasks = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount])
  const hasMore = filtered.length > visibleTasks.length

  // ── Counts for the header ──────────────────────────────────────────────
  // The header counts mirror the assignee filter — not the priority
  // filter — so the user always sees a meaningful headline ("4 open ·
  // 1 overdue") that matches the scope they picked. With the
  // tasksForMyPairs source set the page contains everyone's tasks
  // across every pair; the assignee filter is what tells the header
  // whose count to show.
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])
  const matchesAssigneeFilter = useCallback(
    (task) => assigneeMatches(filters.assignee, task.assignee, currentUserId),
    [filters.assignee, currentUserId],
  )
  const openCount = useMemo(
    () => openTasks.filter(matchesAssigneeFilter).length,
    [openTasks, matchesAssigneeFilter],
  )
  const pastCount = useMemo(
    () => pastTasks.filter(matchesAssigneeFilter).length,
    [pastTasks, matchesAssigneeFilter],
  )
  const overdueCount = useMemo(
    () => openTasks.filter((t) => matchesAssigneeFilter(t) && isOverdue(t, today)).length,
    [openTasks, matchesAssigneeFilter, today],
  )

  // ── Selection helpers ──────────────────────────────────────────────────
  const toggleSelected = useCallback((taskId) => {
    setSelectedIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId],
    )
  }, [])
  const selectAll = useCallback(() => {
    setSelectedIds(visibleTasks.map((t) => t.taskId))
  }, [visibleTasks])
  const clearSelection = useCallback(() => setSelectedIds([]), [])

  // ── Setters that reset pagination on filter / sort change ──────────────
  const setTab = useCallback((next) => {
    setTabState(next)
    setVisibleCount(PAGE_SIZE)
    setSelectedIds([])
  }, [])
  const setFilters = useCallback((next) => {
    setFiltersState((prev) => ({ ...prev, ...(typeof next === 'function' ? next(prev) : next) }))
    setVisibleCount(PAGE_SIZE)
  }, [])
  const setSortField = useCallback((next) => {
    setSortFieldState(next)
    setVisibleCount(PAGE_SIZE)
  }, [])
  const setSortDirection = useCallback((next) => {
    setSortDirectionState(next)
  }, [])
  const loadMore = useCallback(() => setVisibleCount((c) => c + PAGE_SIZE), [])

  // ── Optimistic mutators for bulk actions ───────────────────────────────
  // The bulk handlers in TasksBulkActionBar snapshot, apply locally, fire
  // mutations, and call restoreSnapshot for any taskId that failed.
  const snapshotRef = useRef({ open: [], past: [] })
  const snapshotState = useCallback(() => {
    snapshotRef.current = {
      open: openTasks.map((t) => ({ ...t })),
      past: pastTasks.map((t) => ({ ...t })),
    }
    return snapshotRef.current
  }, [openTasks, pastTasks])

  const applyOptimisticUpdate = useCallback((taskId, patch) => {
    setOpenTasks((prev) => prev.map((t) => (t.taskId === taskId ? { ...t, ...patch } : t)))
    setPastTasks((prev) => prev.map((t) => (t.taskId === taskId ? { ...t, ...patch } : t)))
  }, [])
  const applyOptimisticRemove = useCallback((taskId) => {
    setOpenTasks((prev) => prev.filter((t) => t.taskId !== taskId))
    setPastTasks((prev) => prev.filter((t) => t.taskId !== taskId))
  }, [])
  const restoreSnapshot = useCallback((snapshot) => {
    setOpenTasks(snapshot.open.map((t) => ({ ...t })))
    setPastTasks(snapshot.past.map((t) => ({ ...t })))
  }, [])

  return {
    // tab + data
    tab,
    setTab,
    tasks: visibleTasks,
    totalFiltered: filtered.length,
    openCount,
    pastCount,
    overdueCount,
    loading: loading && (tab === 'open' ? !openLoaded : !pastLoaded),
    error,
    hasMore,
    loadMore,
    refetch,
    // filters + sort
    filters,
    setFilters,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    // selection
    selectedIds,
    toggleSelected,
    selectAll,
    clearSelection,
    // optimistic + subscription wiring
    snapshotState,
    restoreSnapshot,
    applyOptimisticUpdate,
    applyOptimisticRemove,
    handleCreated,
    handleUpdated,
    handleDeleted,
  }
}
